import type { SessionState } from './auth';
import { apiFetch } from './http';

/**
 * `AM-198`: a correct password whose session waits for the second factor (08 §2.10). The token is
 * single use, lives a few minutes and opens nothing but the endpoints below.
 */
export interface MfaChallenge {
  status: typeof MFA_STATUS.required | typeof MFA_STATUS.enrollmentRequired;
  mfaToken: string;
  expiresAt: string;
}

export const MFA_STATUS = {
  required: 'mfa_required',
  enrollmentRequired: 'mfa_enrollment_required',
} as const;

export const MFA_ERROR_CODES = {
  invalidChallenge: 'invalid_mfa_challenge',
  invalidCode: 'invalid_mfa_code',
  alreadyEnrolled: 'mfa_already_enrolled',
  notEnrolled: 'mfa_not_enrolled',
  requiredByPolicy: 'mfa_required_by_policy',
  resetSharedIdentity: 'mfa_reset_shared_identity',
} as const;

/** The secret of a new enrolment, shown once: as text and as the `otpauth://` URI of the QR code. */
export interface MfaEnrollment {
  secret: string;
  otpauthUri: string;
}

/** Recovery codes, shown once; after an enrolment the login completes with them (`session`). */
export interface MfaRecoveryCodes {
  recoveryCodes: string[];
  session?: SessionState | null;
}

export interface MfaStatus {
  enrolled: boolean;
  confirmedAt: string | null;
  recoveryCodesLeft: number;
  required: boolean;
}

/** What `POST /auth/login` answers: the session, or the challenge that stands before it. */
export type LoginAnswer = SessionState | MfaChallenge;

export function isMfaChallenge(answer: LoginAnswer): answer is MfaChallenge {
  return answer.status === MFA_STATUS.required || answer.status === MFA_STATUS.enrollmentRequired;
}

export const mfaApi = {
  verify: (body: {
    mfaToken: string;
    code?: string;
    recoveryCode?: string;
  }): Promise<SessionState> =>
    apiFetch<SessionState>('/api/v1/auth/mfa/verify', { method: 'POST', body }),
  startChallengeEnrollment: (mfaToken: string): Promise<MfaEnrollment> =>
    apiFetch<MfaEnrollment>('/api/v1/auth/mfa/challenge/enrollment', {
      method: 'POST',
      body: { mfaToken },
    }),
  confirmChallengeEnrollment: (body: {
    mfaToken: string;
    code: string;
  }): Promise<MfaRecoveryCodes> =>
    apiFetch<MfaRecoveryCodes>('/api/v1/auth/mfa/challenge/enrollment/confirm', {
      method: 'POST',
      body,
    }),
  status: (signal?: AbortSignal): Promise<MfaStatus> =>
    apiFetch<MfaStatus>('/api/v1/auth/mfa', signal === undefined ? {} : { signal }),
  startEnrollment: (): Promise<MfaEnrollment> =>
    apiFetch<MfaEnrollment>('/api/v1/auth/mfa/enrollment', { method: 'POST' }),
  confirmEnrollment: (code: string): Promise<MfaRecoveryCodes> =>
    apiFetch<MfaRecoveryCodes>('/api/v1/auth/mfa/enrollment/confirm', {
      method: 'POST',
      body: { code },
    }),
  regenerateRecoveryCodes: (code: string): Promise<MfaRecoveryCodes> =>
    apiFetch<MfaRecoveryCodes>('/api/v1/auth/mfa/recovery-codes', {
      method: 'POST',
      body: { code },
    }),
  disable: (code: string): Promise<void> =>
    apiFetch<void>('/api/v1/auth/mfa/disable', { method: 'POST', body: { code } }),
};

/** Query key of `GET /auth/mfa`. */
export const MFA_STATUS_KEY = ['auth', 'mfa'] as const;
